import React, { useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  makeStyles
} from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { components } from 'cng-web-lib'

const {
  button: { CngButton }
} = components

const MAX_PREVIEW_BILLS = 3

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    width: 702,
    maxWidth: '100%'
  },
  titleRoot: {
    padding: theme.spacing(2.5, 3, 1.5)
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  titleText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 700,
    fontSize: 16,
    color: theme.palette.primary.main
  },
  closeBtn: {
    padding: 4,
    color: theme.palette.text.secondary
  },
  infoBox: {
    background: theme.palette.background.sectionOddBg || theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: theme.spacing(0.6, 0),
    '& + &': {
      borderTop: `1px solid ${theme.palette.divider}`
    }
  },
  infoLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    flexShrink: 0,
    marginRight: theme.spacing(2)
  },
  infoValue: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    textAlign: 'right',
    wordBreak: 'break-all'
  },
  infoBanner: {
    display: 'flex',
    gap: theme.spacing(1.5),
    background: theme.palette.info.light + '33',
    border: `1px solid ${theme.palette.info.light}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(1)
  },
  infoIcon: {
    color: theme.palette.info.dark,
    marginTop: 2,
    flexShrink: 0
  },
  infoText: {
    fontSize: 12,
    color: theme.palette.text.primary,
    lineHeight: 1.6
  },
  viewMoreLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    '&:hover': { textDecoration: 'underline' }
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3)
  }
}))

/**
 * SubmitDraftsDialog — confirms submission of draft bill(s) to NAIC.
 *
 * Props:
 *  open       - boolean
 *  row        - upload history row object
 *  onClose    - fn()
 *  onConfirm  - fn()
 *  submitting - boolean
 */
function SubmitDraftsDialog({ open, row, onClose, onConfirm, submitting }) {
  const classes = useStyles()
  const [showAllBills, setShowAllBills] = useState(false)

  if (!row) return null

  const allBills = Array.isArray(row.manifestHeaderIds) ? row.manifestHeaderIds : []
  const billCount = allBills.length
  const previewBills = showAllBills ? allBills : allBills.slice(0, MAX_PREVIEW_BILLS)
  const hasMore = !showAllBills && billCount > MAX_PREVIEW_BILLS

  const billsDisplay = previewBills.join(', ') + (hasMore ? ' ...' : '')

  function handleClose() {
    setShowAllBills(false)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
      disableBackdropClick={submitting}
      disableEscapeKeyDown={submitting}
    >
      {/* Header */}
      <DialogTitle disableTypography classes={{ root: classes.titleRoot }}>
        <Box className={classes.titleRow}>
          <Typography className={classes.titleText}>
            <FontAwesomeIcon icon={['fal', 'paper-plane']} />
            Submit Draft(s) to NAIC
          </Typography>
          <IconButton size='small' className={classes.closeBtn} onClick={handleClose} disabled={submitting}>
            <FontAwesomeIcon icon={['fal', 'xmark']} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent style={{ padding: '20px 24px 12px' }}>
        {/* Detail rows */}
        <Box className={classes.infoBox}>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>File Upload ID:</Typography>
            <Typography className={classes.infoValue}>{row.id || '—'}</Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>File Name:</Typography>
            <Typography className={classes.infoValue}>{row.fileName || '—'}</Typography>
          </Box>
          <Box className={classes.infoRow}>
            <Typography className={classes.infoLabel}>Draft Bill(s):</Typography>
            <Typography className={classes.infoValue} style={{ maxWidth: 320 }}>
              {billCount === 0 ? '—' : (
                <>
                  {billsDisplay}
                  {hasMore && (
                    <>
                      {' '}
                      <span className={classes.viewMoreLink} onClick={() => setShowAllBills(true)}>
                        View More
                      </span>
                    </>
                  )}
                </>
              )}
            </Typography>
          </Box>
        </Box>

        {/* Info banner */}
        <Box className={classes.infoBanner}>
          <FontAwesomeIcon icon={['fal', 'circle-info']} className={classes.infoIcon} />
          <Typography className={classes.infoText}>
            Only the <strong>{billCount}</strong> draft record(s) created from this upload will be submitted to NAIC.
            <br />
            Records already submitted or processed are not affected.
          </Typography>
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box className={classes.footer}>
        <CngButton color='secondary' size='medium' disabled={submitting} onClick={handleClose}>
          Cancel
        </CngButton>
        <CngButton
          color='primary'
          size='medium'
          disabled={submitting}
          onClick={onConfirm}
          startIcon={
            submitting
              ? <FontAwesomeIcon icon={['fal', 'spinner']} spin />
              : <FontAwesomeIcon icon={['fal', 'paper-plane']} />
          }
        >
          {submitting ? 'Submitting...' : 'Confirm & Submit'}
        </CngButton>
      </Box>
    </Dialog>
  )
}

export default SubmitDraftsDialog
